import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { SyncProvider } from '../../lib/sync/provider';
import { LeaveBoard } from './LeaveBoard';

type Source = Pick<SyncProvider, 'status' | 'synced' | 'acks' | 'ownUnsaved' | 'pendingCount' | 'subscribe'>;

/**
 * Edits of this tab the room does not have yet: made offline, or not sent while (re)connecting. An in-app exit keeps
 * the page alive, so the socket still delivers what it was sent: it asks only for these.
 */
export const hasUnsent = (sync: Pick<SyncProvider, 'status' | 'synced' | 'ownUnsaved' | 'pendingCount'>): boolean =>
  sync.pendingCount > 0 || (sync.ownUnsaved && !(sync.status === 'online' && sync.synced));

/**
 * Edits of this tab not written by the room yet (its ack): closing or reloading the tab can drop what the socket had
 * not flushed (seen: a sticky's last words lost), so the browser asks until the room wrote them (P16-3 Review Focus).
 * An older room without acks: unsent only.
 */
export const hasUnsaved = (sync: Pick<SyncProvider, 'status' | 'synced' | 'acks' | 'ownUnsaved' | 'pendingCount'>): boolean =>
  hasUnsent(sync) || (sync.acks && sync.ownUnsaved);

/**
 * Leaving a board (backlog §19 P16-3.4): the browser asks before closing or reloading the tab while edits are not
 * written (`beforeunload`); every in-app exit asks with `LeaveBoard` ("Stay" first) while edits are not sent.
 */
export function useLeaveGuard(sync: Source): { confirmLeave: (go: () => void) => void; dialog: ReactNode } {
  const unsaved = useSyncExternalStore(sync.subscribe, () => hasUnsaved(sync));
  const [pending, setPending] = useState<(() => void) | null>(null);
  useEffect(() => {
    if (!unsaved) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // older browsers need it set to show the prompt
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [unsaved]);
  return {
    confirmLeave: (go) => (hasUnsent(sync) ? setPending(() => go) : go()),
    dialog: pending && (
      <LeaveBoard
        onStay={() => setPending(null)}
        onLeave={() => {
          setPending(null);
          pending();
        }}
      />
    ),
  };
}
